import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Heart, ClipboardList, Settings, LogOut, X, Camera, Save, AlertCircle, ShoppingCart, Trash2, Search, ArrowRight, Package, Clock, CheckCircle, Truck, ChevronRight, Shield, Eye } from 'lucide-react';
import { toggleFavoris, ChercherButton } from './slice';
import { patchCompte, getDataCommandes, getDataComptes, getDataClimats, getDataProjets } from './action';
import { useNavigate, Link } from 'react-router-dom';
import '../styles/userProfileModal.css';

export function UserProfileModal({ isOpen, onClose, hideAdminLink }) {
    const [activeTab, setActiveTab] = useState('favoris');
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [step, setStep] = useState('form'); // 'form' or 'verify'
    const [verificationCode, setVerificationCode] = useState("");
    const [generatedCode, setGeneratedCode] = useState("");

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const loggedUser = JSON.parse(localStorage.getItem('user'));
    const favorisIds = useSelector(state => state.favoris) || [];
    const allProducts = useSelector(state => state.listC) || [];
    const allOrders = useSelector(state => state.commandes) || [];

    const [name, setName] = useState(loggedUser?.name || "");
    const [image, setImage] = useState(loggedUser?.profilePic || "");
    const [password, setPassword] = useState("");
    const [status, setStatus] = useState({ type: '', msg: '' });

    useEffect(() => {
        if (isOpen) {
            dispatch(getDataCommandes());
            dispatch(getDataComptes());
            dispatch(getDataClimats());
            dispatch(getDataProjets());
        }
    }, [isOpen, dispatch]);

    if (!isOpen) return null;

    const handleLogout = () => {
        if (window.confirm("Voulez-vous vous déconnecter ?")) {
            localStorage.removeItem('user');
            onClose();
            navigate("/login");
        }
    };

    const handleToggleFavoris = (climId) => {
        const newFavoris = favorisIds.includes(climId)
            ? favorisIds.filter(id => id !== climId)
            : [...favorisIds, climId];
        dispatch(toggleFavoris(climId));
        dispatch(patchCompte({ id: loggedUser.id, favoris: newFavoris }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setImage(reader.result);
                dispatch(patchCompte({ id: loggedUser.id, profilePic: reader.result }));
                const newUser = { ...loggedUser, profilePic: reader.result };
                localStorage.setItem('user', JSON.stringify(newUser));
            };
            reader.readAsDataURL(file);
        }
    };

    const handleInitiatePasswordChange = (e) => {
        e.preventDefault();
        const code = Math.floor(1000 + Math.random() * 9000).toString();
        setGeneratedCode(code);
        setStep('verify');
        alert(`Un code de vérification a été envoyé à ${loggedUser.email} (Code: ${code})`);
    };

    const handleSaveSettings = async () => {
        if (step === 'verify' && verificationCode !== generatedCode) {
            setStatus({ type: 'error', msg: 'Code incorrect.' });
            return;
        }
        const updatedData = { id: loggedUser.id, name, profilePic: image };
        if (password) updatedData.password = password;
        try {
            await dispatch(patchCompte(updatedData)).unwrap();
            const newUser = { ...loggedUser, ...updatedData };
            localStorage.setItem('user', JSON.stringify(newUser));
            setStatus({ type: 'success', msg: 'Profil mis à jour avec succès !' });
            setStep('form');
            setPassword("");
            setVerificationCode("");
        } catch (error) {
            setStatus({ type: 'error', msg: 'Erreur lors de la mise à jour.' });
        }
    };

    const userOrders = allOrders.filter(order =>
        order.username_Client === loggedUser.name || order.email_Client === loggedUser.email
    );

    const getStatusIcon = (status) => {
        switch (status?.toLowerCase()) {
            case 'en attente': return <Clock size={16} color="#f59e0b" />;
            case 'livrée': return <CheckCircle size={16} color="#10b981" />;
            case 'en cours': return <Truck size={16} color="#3b82f6" />;
            default: return <Package size={16} color="#6b7280" />;
        }
    };

    const renderContent = () => {
        switch (activeTab) {
            case 'favoris':
                const favoriteItems = allProducts.filter(item => favorisIds.includes(item.id));
                return (
                    <div className="modal-tab-content">
                        <div className="tab-header">
                            <h2>Mes Favoris</h2>
                        </div>
                        {favoriteItems.length === 0 ? (
                            <div className="empty-state">
                                <Heart size={48} color="#ccc" />
                                <p>Aucun favori pour le moment.</p>
                            </div>
                        ) : (
                            <div className="list-stack-rows">
                                {favoriteItems.map(item => (
                                    <div key={item.id} className="row-item favoris-row">
                                        <div className="row-img-square">
                                            <img src={item.image} alt="" />
                                        </div>
                                        <div className="row-details">
                                            <h4>{item.nomClimatisseur}</h4>
                                            <span className="row-price">{item.tailles[0].prix} DH</span>
                                        </div>
                                        <div className="row-actions">
                                            <button className="btn-row-view" onClick={() => { navigate(`/infoClimatisseur/${item.id}`); onClose(); }}>Voir</button>
                                            <button className="btn-row-del" onClick={() => handleToggleFavoris(item.id)}><Trash2 size={16} /></button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                );

            case 'missions':
                return (
                    <div className="modal-tab-content">
                        <div className="tab-header">
                            <h2>Mes Commandes</h2>
                        </div>
                        {userOrders.length === 0 ? (
                            <div className="empty-state">
                                <Package size={48} color="#ccc" />
                                <p>Aucune commande passée.</p>
                            </div>
                        ) : (
                            <div className="list-stack-rows">
                                {userOrders.map(order => (
                                    <div key={order.id} className="row-item order-row">
                                        <div className="order-main-info">
                                            <span className="order-id">#{order.id}</span>
                                        </div>
                                        <div className="row-details">
                                            <div className="order-time-group">
                                                <Clock size={16} color="#f59e0b" fill="#f59e0b10" />
                                                <span className="order-date">{order.date}</span>
                                            </div>
                                            <span className="order-status-lbl">{order.statut}</span>
                                        </div>
                                        <div className="row-amount-info">
                                            <span className="order-amount">{order.total} DH</span>
                                        </div>
                                        <div className="row-actions">
                                            <button className="btn-row-view" onClick={() => setSelectedOrder(order)}>Détails</button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                );
            case 'settings':
                return (
                    <div className="modal-tab-content">
                        <div className="tab-header">
                            <h2>Paramètres</h2>
                        </div>
                        <div className="settings-mini-form">
                            <div className="avatar-edit">
                                <img src={image || `https://ui-avatars.com/api/?name=${name}&background=700000&color=fff`} alt="" />
                                <label className="cam-btn"><Camera size={16} /><input type="file" hidden onChange={handleImageChange} accept="image/*" /></label>
                            </div>
                            {step === 'form' ? (
                                <>
                                    <div className="form-group-mini">
                                        <label>Nom Complet</label>
                                        <input type="text" value={name} onChange={e => setName(e.target.value)} required />
                                    </div>
                                    <div className="form-group-mini">
                                        <label>Nouveau mot de passe</label>
                                        <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Laisser vide pour garder l'actuel" />
                                    </div>
                                    <button onClick={password ? handleInitiatePasswordChange : handleSaveSettings} className="btn-save-mini">
                                        <Save size={16} /> {password ? "Changer le mot de passe" : "Enregistrer"}
                                    </button>
                                </>
                            ) : (
                                <>
                                    <div className="form-group-mini">
                                        <label>Code envoyé à {loggedUser.email}</label>
                                        <input type="text" value={verificationCode} onChange={e => setVerificationCode(e.target.value)} placeholder="Entrez le code" required />
                                    </div>
                                    <div className="verification-actions">
                                        <button onClick={handleSaveSettings} className="btn-save-mini">Confirmer le code</button>
                                        <button onClick={() => setStep('form')} className="btn-cancel-mini">Annuler</button>
                                    </div>
                                </>
                            )}
                            {status.msg && <div className={`alert-mini ${status.type}`}>{status.msg}</div>}
                        </div>
                    </div>
                );
            default: return null;
        }
    };

    return (
        <div className="profile-modal-overlay" onClick={onClose}>
            <div className="profile-modal-container" onClick={e => e.stopPropagation()}>
                <div className="modal-sidebar">
                    <div className="sidebar-top-group">
                        <div className="sidebar-user-info">
                            <div className="avatar-main">
                                <img src={image || `https://ui-avatars.com/api/?name=${name}&background=700000&color=fff`} alt="User" />
                            </div>
                            <h3>{name || loggedUser.email.split('@')[0]}</h3>
                            <p>{loggedUser.email}</p>
                        </div>
                        <nav className="modal-nav">
                            <button className={activeTab === 'favoris' ? 'active' : ''} onMouseEnter={() => setActiveTab('favoris')}>
                                <Heart size={18} /> <span>Mes Favoris</span>
                            </button>
                            <button className={activeTab === 'missions' ? 'active' : ''} onMouseEnter={() => setActiveTab('missions')}>
                                <ClipboardList size={18} /> <span>Commandes</span>
                            </button>
                            <button className={activeTab === 'settings' ? 'active' : ''} onMouseEnter={() => setActiveTab('settings')}>
                                <Settings size={18} /> <span>Paramètres</span>
                            </button>
                        </nav>
                    </div>

                    <div className="sidebar-bottom-group">
                        {(loggedUser.role === 'admin' && !hideAdminLink) && (
                            <Link to="/dashbordPage" className="modal-admin-link" onClick={onClose}>
                                <Shield size={18} /> <span>Retour Dashboard</span>
                            </Link>
                        )}
                        <button className="modal-logout-btn" onClick={handleLogout}>
                            <LogOut size={18} /> <span>Déconnexion</span>
                        </button>
                    </div>
                </div>
                <div className="modal-main-content">
                    <button className="modal-close-btn" onClick={onClose}><X size={24} /></button>
                    {renderContent()}
                </div>
            </div>

            {/* Order Detail Modal Overlay */}
            {selectedOrder && (
                <div className="mini-order-modal-overlay" onClick={() => setSelectedOrder(null)}>
                    <div className="mini-order-modal" onClick={e => e.stopPropagation()}>
                        <div className="mini-modal-header">
                            <h3>Commande #{selectedOrder.id}</h3>
                            <button onClick={() => setSelectedOrder(null)}><X size={20} /></button>
                        </div>
                        <div className="mini-modal-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                            <div className="detail-row"><span>Date</span> <span>{selectedOrder.date}</span></div>
                            <div className="detail-row"><span>Total</span> <strong>{selectedOrder.total} DH</strong></div>
                            <div className="detail-row"><span>Statut</span> <span className="status-badge">{selectedOrder.statut}</span></div>
                            <div className="order-items-list-mini">
                                <p className="section-p">Produits :</p>
                                {(selectedOrder.produits || selectedOrder.panier_Client)?.map((p, idx) => (
                                    <div key={idx} className="mini-product-item">
                                        <span>{(p.nom || p.nomClimatisseur)} x {p.quantite}</span>
                                        <span>{(p.prix * p.quantite) || p.prix_total} DH</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
